import { Icon, Kbd, cn } from "@sagot/ui";
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type SyntheticEvent } from "react";
import { COMPOSER_COUNTER_FROM, COMPOSER_MAX_CHARS } from "../constants";
import type { WorkflowInputTraits } from "../workflow/step";
import { primaryActionClass, useChatTheme } from "./chat-theme";

export interface ComposerLabels {
  input: string;
  placeholder: string;
  send: string;
  stop: string;
  hintSend: string;
  hintNewline: string;
  counter: (used: number, max: number) => string;
}

/**
 * The composer answers a workflow question (`collect_reply`): one line, the keyboard of the format, the
 * question as its accessible name. The host sends the text with `answerWorkflowStep`.
 */
export interface ComposerReply extends Pick<WorkflowInputTraits, "type" | "inputMode" | "autoComplete"> {
  /** Accessible name of the field, e.g. "Answer: What is your student number?". */
  label: string;
  placeholder: string;
  maxLength: number;
  /** The server asked again after an invalid value: this says so (polite) and the cursor goes back to the field. */
  error?: string;
}

const DEFAULT_LABELS: ComposerLabels = {
  input: "Message",
  placeholder: "Ask about admissions, fees, documents…",
  send: "Send",
  stop: "Stop generating",
  hintSend: "to send",
  hintNewline: "new line",
  counter: (used, max) => `${used} / ${max}`,
};

export interface ComposerProps {
  onSend: (text: string) => void;
  onStop?: () => void;
  /** An answer is streaming: input locks, Send becomes Stop. */
  streaming?: boolean;
  /** Gate pending / offline. */
  disabled?: boolean;
  /**
   * A workflow question needs a button or a value first: typing is off and this text says why
   * (visible above the field, "Choose an option above").
   */
  lockedHint?: string;
  /** A `collect_reply` question is open: the composer is its answer field (see `ComposerReply`). */
  reply?: ComposerReply;
  maxLength?: number;
  /** `app` (web app card) or `compact` (playground / widget row). */
  variant?: "app" | "compact";
  /** Attachments slot — kept hidden until attachments ship. */
  attachments?: ReactNode;
  showAttachments?: boolean;
  /** Controlled draft (kept on errors so nothing is lost). */
  value?: string;
  onValueChange?: (value: string) => void;
  labels?: Partial<ComposerLabels>;
  className?: string;
}

const MAX_HEIGHT_PX = { app: 192, compact: 128 } as const;
const GEO_HEIGHT = "--geo-height";

/** Auto-growing textarea: Enter sends, Shift+Enter adds a line, 2,000-char limit with counter. */
export function Composer({
  onSend,
  onStop,
  streaming = false,
  disabled = false,
  lockedHint,
  reply,
  maxLength: messageMaxLength = COMPOSER_MAX_CHARS,
  variant = "app",
  attachments,
  showAttachments = false,
  value,
  onValueChange,
  labels,
  className,
}: ComposerProps) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const { themed } = useChatTheme();
  const id = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyRef = useRef<HTMLInputElement>(null);
  const maxLength = reply?.maxLength ?? messageMaxLength;
  const [internal, setInternal] = useState("");
  const text = value ?? internal;
  const setText = (next: string) => {
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };

  // Auto-grow: measure with the height var reset, then pin it (geometry custom property only).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.setProperty(GEO_HEIGHT, "auto");
    const measured = el.scrollHeight;
    if (measured > 0) el.style.setProperty(GEO_HEIGHT, `${Math.min(measured, MAX_HEIGHT_PX[variant])}px`);
    else el.style.removeProperty(GEO_HEIGHT);
  }, [text, variant]);

  // The server asked again: put the cursor back where the visitor fixes the answer.
  const replyError = reply?.error;
  useEffect(() => {
    if (replyError) replyRef.current?.focus();
  }, [replyError]);

  const hinted = lockedHint !== undefined;
  const locked = disabled || streaming || hinted;
  const canSend = !locked && text.trim().length > 0;
  const showCounter = text.length >= maxLength * COMPOSER_COUNTER_FROM;

  const submit = (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (!canSend) return;
    onSend(text.trim());
    setText("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <>
      {reply?.error ? (
        <p id={`${id}-reply-error`} role="status" className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold text-danger">
          <Icon name="alert-circle" className="size-3.5 shrink-0" />
          {reply.error}
        </p>
      ) : null}
      {hinted ? (
        <p id={`${id}-locked`} role="status" className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold text-fg-muted">
          <Icon name="info" className="size-3.5 shrink-0" />
          {lockedHint}
        </p>
      ) : null}
      <form
        onSubmit={submit}
        className={cn(
          "border border-line-strong bg-surface focus-within:border-primary",
          variant === "app" ? "rounded-xl p-2 shadow-card" : "flex items-end gap-2 rounded-lg px-2 py-2",
          className,
        )}
      >
        <label htmlFor={`${id}-input`} className="sr-only">
          {reply ? reply.label : l.input}
        </label>
        {showAttachments ? attachments : null}
        {reply ? (
          <input
            ref={replyRef}
            id={`${id}-input`}
            type={reply.type}
            inputMode={reply.inputMode}
            autoComplete={reply.autoComplete}
            value={text}
            maxLength={maxLength}
            disabled={disabled}
            readOnly={streaming}
            aria-describedby={cn(reply.error && `${id}-reply-error`, `${id}-hint`, `${id}-count`)}
            aria-invalid={reply.error ? true : undefined}
            placeholder={reply.placeholder}
            onChange={(event) => {
              setText(event.target.value);
            }}
            className={cn(
              "block w-full bg-transparent text-fg outline-none placeholder:text-fg-subtle",
              variant === "app" ? "min-h-11 px-2.5 py-2 text-[15px]" : "min-h-11 flex-1 px-1 py-1.5 text-sm",
            )}
          />
        ) : (
          <textarea
            ref={textareaRef}
            id={`${id}-input`}
            rows={1}
            value={text}
            maxLength={maxLength}
            disabled={disabled || hinted}
            readOnly={streaming}
            aria-describedby={hinted ? `${id}-locked ${id}-hint ${id}-count` : `${id}-hint ${id}-count`}
            placeholder={l.placeholder}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={onKeyDown}
            className={cn(
              "block h-[var(--geo-height,auto)] w-full resize-none bg-transparent text-fg outline-none placeholder:text-fg-subtle",
              variant === "app" ? "max-h-48 min-h-10 px-2.5 py-2 text-[15px] leading-relaxed" : "max-h-32 min-h-8 flex-1 px-1 py-1.5 text-sm",
            )}
          />
        )}
        <div className={cn("flex items-center gap-1", variant === "compact" && "contents")}>
          <span id={`${id}-hint`} className={cn("ml-1 hidden text-[11px] text-fg-subtle", variant === "app" && "sm:inline")}>
            <Kbd>Enter</Kbd> {l.hintSend}
            {reply ? null : (
              <>
                {" "}
                · <Kbd>Shift</Kbd> + <Kbd>Enter</Kbd> {l.hintNewline}
              </>
            )}
          </span>
          <span id={`${id}-count`} aria-live="polite" className={cn("ml-auto text-[11px] tabular-nums", showCounter ? "text-fg-subtle" : "sr-only", text.length >= maxLength && "font-semibold text-danger")}>
            {showCounter ? l.counter(text.length, maxLength) : ""}
          </span>
          {streaming && onStop ? (
            <button type="button" aria-label={l.stop} onClick={onStop} className="btn btn-secondary btn-icon btn-sm">
              <Icon name="square" />
            </button>
          ) : (
            <button type="submit" aria-label={l.send} disabled={!canSend} className={cn(primaryActionClass(themed), "btn-icon btn-sm")}>
              <Icon name={variant === "app" ? "arrow-up" : "send"} />
            </button>
          )}
        </div>
      </form>
    </>
  );
}
